import { PatientContext } from "$routes/_lib/patient-context";
import { RequireAuth } from "$routes/_lib/require-auth";
import { useQuerystring } from "$routes/_lib/use-querystring";
import { InvestigationActivitiesProvider } from "$routes/patient/[:patientId]/_lib/investigations/investigation-activities-provider";
import { Role } from "elephant-web/gql";
import { lazy } from "react";
import { Route, Routes } from "react-router-dom";
import { AntenatalCareRoutes } from "./antenatal-care/routes";
import { NewConsultationRoutes } from "./consultation/routes";
import { PatientDetailsPage } from "./details/page";
import { INPATIENT_AUTHORISED_ROLES } from "./inpatient/links";
import { InpatientRoutes } from "./inpatient/routes";
import { PatientDetailsLayout } from "./layout";
import { ReferralRoutes } from "./referrals/routes";
import { VaccinationRoutes } from "./vaccinations/routes";

const CREATE_CONSULTATION_ROLES = [
  Role.Clinic,
  Role.Doctor,
  Role.Elephant,
  Role.Nurse,
  Role.Root,
  Role.Sonographer,
];

// ------ Dispense Medications ------
const PrescriptionsPage = lazy(() =>
  import(
    /* webpackChunkName: "Prescriptions" */ /* webpackPrefetch: true */ "./prescriptions/page"
  ).then((module) => ({ default: module.Page })),
);

// ------ Investigations ------
const NewInvestigationPage = lazy(() =>
  import(
    /* webpackChunkName: "NewInvestigation" */ /* webpackPrefetch: true */ "./investigations/new/page"
  ).then((module) => ({ default: module.NewInvestigationPage })),
);

const {
  Doctor,
  Elephant,
  Nurse,
  Technician,
  Pharmacist,
  Dispenser,
  Root,
  Sonographer,
} = Role;

export const PatientRoutes = () => {
  const query = useQuerystring();
  // Investigation complete page is unique and has the query param 'patient' for patientId
  const patientId = query.patientUuid || query.patient;

  return (
    <PatientContext.Provider value={{ uuid: patientId }}>
      <InvestigationActivitiesProvider patientId={patientId}>
        <Routes>
          <Route element={<PatientDetailsLayout />}>
            <Route
              path="details"
              element={
                <RequireAuth except={[Root]}>
                  <PatientDetailsPage />
                </RequireAuth>
              }
            />
            <Route
              path="prescriptions/*"
              element={
                <RequireAuth
                  only={[Doctor, Elephant, Nurse, Pharmacist, Dispenser]}
                >
                  <PrescriptionsPage />
                </RequireAuth>
              }
            />
            <Route
              path="consultations/*"
              element={
                <RequireAuth only={CREATE_CONSULTATION_ROLES}>
                  <NewConsultationRoutes />
                </RequireAuth>
              }
            />
            <Route
              path="vaccinations/*"
              element={
                <RequireAuth only={CREATE_CONSULTATION_ROLES}>
                  <VaccinationRoutes />
                </RequireAuth>
              }
            />
            <Route
              path="referrals/*"
              element={
                <RequireAuth only={[Role.Sonographer]}>
                  <ReferralRoutes />
                </RequireAuth>
              }
            />
            <Route
              path="inpatient/*"
              element={
                <RequireAuth only={INPATIENT_AUTHORISED_ROLES}>
                  <InpatientRoutes />
                </RequireAuth>
              }
            />
            <Route path="investigations">
              <Route
                path="new"
                element={
                  <RequireAuth
                    only={[Doctor, Elephant, Nurse, Technician, Sonographer]}
                  >
                    <NewInvestigationPage />
                  </RequireAuth>
                }
              />
            </Route>
            <Route
              path="antenatal-care/*"
              element={
                <RequireAuth only={[Doctor, Nurse, Sonographer]}>
                  <AntenatalCareRoutes />
                </RequireAuth>
              }
            />
            {/* Backward compatibility routes */}
            <Route
              path="create-anc"
              element={
                <RequireAuth only={[Doctor, Nurse, Sonographer]}>
                  <AntenatalCareRoutes />
                </RequireAuth>
              }
            />
            <Route path="anc/*">
              <Route
                path=":encounterId"
                element={
                  <RequireAuth only={[Doctor, Nurse, Sonographer]}>
                    <AntenatalCareRoutes />
                  </RequireAuth>
                }
              />
            </Route>
          </Route>
        </Routes>
      </InvestigationActivitiesProvider>
    </PatientContext.Provider>
  );
};
